/**
 * WorkflowRunDetailPage — /agents/runs/:runId
 * Demo pages: /ai (run status, cost, duration as summary numbers) and /profile (DetailPage with tabs). The tab bodies
 * (graph, summary, logs, timeline, outputs, cost, error) live in ./run-detail/ and behave as before; the active tab is
 * in the URL (`?tab=`), a failed run opens on Error. Run actions need CanManageWorkflows (POST .../actions).
 */
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle, Clock, DollarSign, FileText, List, Workflow } from 'lucide-react';
import { Button, Callout, Skeleton, StatStrip, useToast } from '../../components/ui/index.js';
import { DetailPage } from '../../components/page/index.js';
import { describeQueryError } from '../../components/ui/QueryBoundary.js';
import { useTabParam } from '../../hooks/useTabParam.js';
import {
  getAgentWorkflowRunDebuggerOptions,
  getAgentWorkflowRunDebuggerQueryKey,
  runAgentWorkflowRunActionMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import type { RunAgentWorkflowRunActionData } from '../../generated/api/types.gen.js';
import { RunDetailActions } from './run-detail/RunDetailActions.js';
import { RunDetailBadges, RunDetailMeta } from './run-detail/RunDetailHeader.js';
import { runStatCells } from './run-detail/run-stat-cells.js';
import { RunGraphTab } from './run-detail/RunGraphTab.js';
import { RunSummaryTab } from './run-detail/RunSummaryTab.js';
import { RunLogsTab } from './run-detail/RunLogsTab.js';
import { RunOutputsTab } from './run-detail/RunOutputsTab.js';
import { RunCostTab } from './run-detail/RunCostTab.js';
import { RunErrorTab } from './run-detail/RunErrorTab.js';
import { RunTimelineTab } from './run-detail/RunTimelineTab.js';
import { type DebugPayload, type DetailLevel, findFailedStep } from './run-detail/run-detail-types.js';

const DETAIL_TABS = [
  { id: 'graph', label: 'Graph', icon: Workflow },
  { id: 'summary', label: 'Summary', icon: CheckCircle },
  { id: 'logs', label: 'Logs and debug', icon: List },
  { id: 'timeline', label: 'Timeline', icon: Clock },
  { id: 'outputs', label: 'Outputs', icon: FileText },
  { id: 'cost', label: 'Cost', icon: DollarSign },
  { id: 'error', label: 'Error', icon: AlertTriangle },
] as const;
const TAB_IDS = DETAIL_TABS.map((t) => t.id);

const CRUMBS = [{ label: 'Run history', to: '/agents/runs' }];

export const WorkflowRunDetailPage: React.FC = () => {
  const { runId = '' } = useParams<{ runId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { notify } = useToast();

  const [detail, setDetail] = useState<DetailLevel>('debug');
  const [selectedStepId, setSelectedStepId] = useState<string | null>(null);

  const query = useQuery({
    ...getAgentWorkflowRunDebuggerOptions({ path: { runId }, query: { detail } }),
    placeholderData: (prev) => prev,
    // A running run refreshes every 3 seconds.
    refetchInterval: (q) => (q.state.data?.run?.status === 'running' ? 3000 : false),
    // The page's step components are written against the narrower `DebugPayload` shape of this same response.
    select: (payload) => payload as unknown as DebugPayload,
  });
  const payload = query.data;
  const run = payload?.run;
  const steps = payload?.graph.steps ?? [];

  const [activeTab, setActiveTab] = useTabParam(TAB_IDS, run?.status === 'failed' ? 'error' : 'graph');

  const actionMutation = useMutation({
    ...runAgentWorkflowRunActionMutation(),
    onSuccess: (result) => {
      if (result.taskDraftUrl) {
        navigate(result.taskDraftUrl);
        return;
      }
      notify(result.message ?? `${result.action} ${result.status}`, 'success');
      queryClient.invalidateQueries({
        queryKey: getAgentWorkflowRunDebuggerQueryKey({ path: { runId }, query: { detail } }),
      });
    },
    onError: (error) => notify(error.message, 'danger'),
  });

  const selectedStep = steps.find((s) => s.stepId === selectedStepId) ?? findFailedStep(steps) ?? steps[0];

  const runAction = (action: string, stepId?: string) => {
    actionMutation.mutate({
      path: { runId },
      body: {
        action: action as NonNullable<RunAgentWorkflowRunActionData['body']>['action'],
        stepId,
        reason: selectedStep?.error ?? run?.error ?? undefined,
      },
    });
  };

  if (query.isPending) {
    return (
      <DetailPage title="Run" breadcrumbs={CRUMBS}>
        <Skeleton height={96} rounded="rounded-lg" />
        <Skeleton height={320} rounded="rounded-lg" />
      </DetailPage>
    );
  }
  if (!payload || !run) {
    return (
      <DetailPage title="Run" breadcrumbs={CRUMBS}>
        <Callout
          tone="danger"
          title={describeQueryError(query.error, 'the workflow run')}
          actions={
            <Button size="sm" variant="secondary" onClick={() => query.refetch()}>
              Retry
            </Button>
          }
        />
      </DetailPage>
    );
  }

  return (
    <DetailPage
      title={run.workflowName}
      breadcrumbs={CRUMBS}
      badges={<RunDetailBadges payload={payload} />}
      meta={<RunDetailMeta run={run} />}
      actions={
        <RunDetailActions
          payload={payload}
          detail={detail}
          onDetailChange={setDetail}
          onAction={runAction}
          actionPending={actionMutation.isPending}
        />
      }
      alert={
        query.isError ? (
          <Callout tone="warning">
            {detail === 'full'
              ? 'Full I/O requires workflow management permission. Showing the last available safe view.'
              : describeQueryError(query.error, 'the workflow run')}
          </Callout>
        ) : undefined
      }
      tabs={DETAIL_TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
    >
      <StatStrip cells={runStatCells(run)} />

      {activeTab === 'graph' && (
        <RunGraphTab
          payload={payload}
          activeStepId={selectedStepId ?? findFailedStep(steps)?.stepId ?? null}
          onSelectStep={setSelectedStepId}
        />
      )}
      {activeTab === 'summary' && <RunSummaryTab payload={payload} />}
      {activeTab === 'logs' && <RunLogsTab payload={payload} detail={detail} />}
      {activeTab === 'timeline' && <RunTimelineTab payload={payload} />}
      {activeTab === 'outputs' && <RunOutputsTab payload={payload} />}
      {activeTab === 'cost' && <RunCostTab payload={payload} />}
      {activeTab === 'error' && (
        <RunErrorTab payload={payload} onAction={runAction} actionPending={actionMutation.isPending} />
      )}
    </DetailPage>
  );
};

export default WorkflowRunDetailPage;
